import React from 'react';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, componentStack: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        console.error('[ErrorBoundary]', error, errorInfo);
        // Kept for the dev fallback: the message alone ("reading 'length'")
        // says what broke but not WHERE — the component stack does.
        this.setState({ componentStack: errorInfo?.componentStack || null });
    }

    handleReset = () => {
        this.setState({ hasError: false, error: null, componentStack: null });
    };

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback({
                    error: this.state.error,
                    componentStack: this.state.componentStack,
                    reset: this.handleReset,
                });
            }

            return (
                <div className="h-screen w-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
                    <div className="max-w-md text-center px-6">
                        <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
                            <svg className="w-7 h-7 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                            </svg>
                        </div>
                        <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100 mb-2">
                            Something went wrong
                        </h2>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
                            An unexpected error occurred. Try refreshing the page or going back.
                        </p>
                        <div className="flex items-center justify-center gap-3">
                            <button
                                onClick={this.handleReset}
                                className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                            >
                                Try Again
                            </button>
                            <button
                                onClick={() => window.location.href = '/'}
                                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
                            >
                                Go Home
                            </button>
                        </div>
                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <pre className="mt-6 p-3 text-left text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/10 rounded-lg overflow-auto max-h-40">
                                {this.state.error.message}
                                {'\n'}
                                {this.state.error.stack}
                            </pre>
                        )}
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;

// ─── Scoped fallback for per-panel boundaries ─────────────────────────────
// Renders in the available content area (no h-screen / w-screen) so that
// when an editor panel crashes, the surrounding sidebar / header / nav
// chrome stays visible and usable.
const PanelErrorFallback = ({ error, componentStack, reset, panelName }) => (
    <div className="w-full h-full flex items-center justify-center p-8 bg-slate-50 dark:bg-slate-950 grey:bg-zinc-50">
        <div className="max-w-md text-center">
            <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-red-50 dark:bg-red-900/20 grey:bg-red-50 flex items-center justify-center">
                <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
            </div>
            <h3 className="text-base font-bold text-slate-800 dark:text-slate-100 grey:text-zinc-800 mb-1">
                Couldn't render {panelName || 'this panel'}
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 grey:text-zinc-600 mb-4">
                An unexpected error occurred. The rest of the studio is still usable — try again, or navigate elsewhere.
            </p>
            <button
                onClick={reset}
                className="px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            >
                Try Again
            </button>
            {process.env.NODE_ENV === 'development' && error && (
                <pre className="mt-4 p-2 text-left text-[10px] text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/10 rounded overflow-auto max-h-32">
                    {error.message}
                    {componentStack ? `\n${componentStack}` : ''}
                </pre>
            )}
        </div>
    </div>
);

// Convenience wrapper: scopes errors to a panel and renders an inline fallback
// without blanking the surrounding viewport chrome.
export const PanelErrorBoundary = ({ panelName, children }) => (
    <ErrorBoundary fallback={({ error, componentStack, reset }) => (
        <PanelErrorFallback panelName={panelName} error={error} componentStack={componentStack} reset={reset} />
    )}>
        {children}
    </ErrorBoundary>
);
